import axios from "axios";
import localforage from "localforage";

const CACHE_KEY_PREFIX = 'image_cache:';

const imageStore = localforage.createInstance({
    name: 'knockout-image-cache',
    storeName: 'images',
});

export default class ImageCacheManager
{
    /**
     * Download an image from S3 and cache it locally as a Blob in IndexedDB.
     * Returns an object URL for the cached blob, or the remote URL as fallback.
     */
    static async cacheImage(referenceId, image, destination)
    {
        const cacheKey = CACHE_KEY_PREFIX + destination + ':' + image;
        const remotePath = import.meta.env.VITE_IMAGES_PATH + referenceId + '/' + image;

        const cached = await imageStore.getItem(cacheKey);
        if (cached) {
            return URL.createObjectURL(cached);
        }

        try {
            const response = await axios.get(remotePath, { responseType: 'arraybuffer' });
            const mime = ImageCacheManager.getMimeType(image);
            const blob = new Blob([response.data], { type: mime });
            await imageStore.setItem(cacheKey, blob);
            return URL.createObjectURL(blob);
        } catch (e) {
            console.error(`Failed to cache image: ${remotePath}`, e);
            return remotePath;
        }
    }

    /**
     * Retrieve a cached image URL.
     * Returns an object URL for the cached blob if available, null otherwise.
     */
    static async getCachedImage(image, destination)
    {
        const cacheKey = CACHE_KEY_PREFIX + destination + ':' + image;
        const cached = await imageStore.getItem(cacheKey);

        if (cached) {
            return URL.createObjectURL(cached);
        }

        return null;
    }

    /**
     * Get the image src for a cached image.
     * Returns an object URL for the cached blob or falls back to remote URL.
     */
    static async getImageSrc(referenceId, image, destination)
    {
        const cached = await ImageCacheManager.getCachedImage(image, destination);
        if (cached) {
            return cached;
        }
        return import.meta.env.VITE_IMAGES_PATH + referenceId + '/' + image;
    }

    /**
     * Cache all chest images from the references data.
     */
    static async cacheChestImages(referenceId, chests)
    {
        if (!chests || !Array.isArray(chests)) {
            return;
        }

        const promises = chests
            .filter(chest => chest.image)
            .map(chest => ImageCacheManager.cacheImage(referenceId, chest.image, 'chests'));

        await Promise.allSettled(promises);
    }

    /**
     * Cache all sale images from the sales data.
     * Also prunes stale sale images that are no longer current.
     */
    static async cacheSaleImages(referenceId, sales)
    {
        if (!sales || !Array.isArray(sales)) {
            return;
        }

        const imageFields = ['image_1', 'image_2', 'image_3'];
        const promises = [];

        sales.forEach(sale => {
            imageFields.forEach(field => {
                if (sale[field]) {
                    promises.push(ImageCacheManager.cacheImage(referenceId, sale[field], 'sales'));
                }
            });
        });

        await Promise.allSettled(promises);
        await ImageCacheManager.pruneSaleCache(sales);
    }

    /**
     * Cache all design images (chests) from references data.
     * Also prunes stale cached images that are no longer current.
     */
    static async cacheDesignImages(referenceId, data)
    {
        if (!data || !data.design) {
            return;
        }

        await ImageCacheManager.cacheChestImages(referenceId, data.design.chest);
        await ImageCacheManager.pruneCache(data.design.chest, null);
    }

    /**
     * Determine MIME type from filename extension.
     */
    static getMimeType(filename)
    {
        const ext = filename.split('.').pop().toLowerCase();
        const types = {
            'png': 'image/png',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'gif': 'image/gif',
            'webp': 'image/webp',
            'svg': 'image/svg+xml',
        };
        return types[ext] || 'image/png';
    }

    /**
     * Clear all cached images from IndexedDB.
     */
    static async clearCache()
    {
        await imageStore.clear();
    }

    /**
     * Remove cached images that are no longer current.
     * Compares cached entries against the provided current image sets
     * and deletes any that are stale.
     */
    static async pruneCache(chests, sales)
    {
        const validKeys = new Set();

        // Collect valid chest image keys
        if (chests && Array.isArray(chests)) {
            chests.forEach(chest => {
                if (chest.image) {
                    validKeys.add(CACHE_KEY_PREFIX + 'chests:' + chest.image);
                }
            });
        }

        // Collect valid sale image keys
        if (sales && Array.isArray(sales)) {
            const imageFields = ['image_1', 'image_2', 'image_3'];
            sales.forEach(sale => {
                imageFields.forEach(field => {
                    if (sale[field]) {
                        validKeys.add(CACHE_KEY_PREFIX + 'sales:' + sale[field]);
                    }
                });
            });
        }

        // Remove any cached images not in the valid set
        const keys = await imageStore.keys();
        const keysToRemove = [];

        for (const key of keys) {
            if (!key.startsWith(CACHE_KEY_PREFIX)) continue;

            const isChestKey = key.startsWith(CACHE_KEY_PREFIX + 'chests:');
            const isSaleKey = key.startsWith(CACHE_KEY_PREFIX + 'sales:');

            if (isChestKey && chests && !validKeys.has(key)) {
                keysToRemove.push(key);
            }
            if (isSaleKey && sales && !validKeys.has(key)) {
                keysToRemove.push(key);
            }
        }

        await Promise.all(keysToRemove.map(key => imageStore.removeItem(key)));

        if (keysToRemove.length > 0) {
            console.log(`ImageCacheManager: pruned ${keysToRemove.length} stale image(s)`);
        }
    }

    /**
     * Prune only stale sale images.
     */
    static async pruneSaleCache(sales)
    {
        await ImageCacheManager.pruneCache(null, sales);
    }
}
